// Core packages

// Third party packages

// Custom packages

/**
 * Pure helpers of the "Cerca il documento" box of the insert dialog (document-insert-dialog), the one
 * that the "+" next to each section subtitle opens on the property documents page.
 *
 * The box narrows the list as it does on addNewDocuments: a document that stops matching is hidden
 * and loses its check. The dialog saves its own `selectedDocs` array, not the checks on screen, so
 * every document unchecked here has to leave that array too, the same way a click takes it out.
 *
 * @since 3.0.0
 */

/** What the search reads and writes on a document of the dialog list */
export interface SearchableDocument {
  /** Document code (e.g. "AA1"); missing on the group headings of the "Unità immobiliari" list */
  key?: string;
  /** Title in both languages: the one on screen follows the dialog language */
  translation?: { it?: string; en?: string };
  /** UI only: false while the search hides the document */
  visible?: boolean;
  /** UI only: state of the checkbox */
  value?: boolean;
}

/**
 * True when the title on screen or the code of the document contains the searched text.
 * The text is matched literally and regardless of case: "(" and "." are plain characters, they
 * neither break the search nor stand for any character. An empty text matches every document.
 *
 * @param doc document of the dialog list
 * @param search text typed in the search box
 * @param lang dialog language: "it" reads the Italian title, any other the English one, as the template does
 */
export function matchesDocumentSearch(
  doc: SearchableDocument,
  search: string,
  lang: string
): boolean {
  const text = (search || "").trim().toLowerCase();
  if (!text) {
    return true;
  }
  const title = doc.translation
    ? (lang === "it" ? doc.translation.it : doc.translation.en)
    : undefined;
  return (
    (typeof title === "string" && title.toLowerCase().indexOf(text) > -1) ||
    (typeof doc.key === "string" && doc.key.toLowerCase().indexOf(text) > -1)
  );
}

/**
 * Shows the documents that match the search and hides the others; a hidden document loses its check.
 * Entries without a key are the group headings of the "Unità immobiliari" list: they cannot be
 * checked and always stay on screen.
 *
 * @param docs documents of the dialog list, changed in place
 * @param search text typed in the search box
 * @param lang dialog language
 * @returns the documents that were checked and are now hidden. The caller has to take them out of its
 * own selection as well, otherwise they would still be saved while nothing on screen shows them.
 */
export function applyDocumentSearch<T extends SearchableDocument>(
  docs: T[] | null | undefined,
  search: string,
  lang: string
): T[] {
  const uncheckedDocs: T[] = [];
  if (!Array.isArray(docs)) {
    return uncheckedDocs;
  }
  docs.forEach((doc) => {
    if (!doc || !doc.key) {
      return;
    }
    doc.visible = matchesDocumentSearch(doc, search, lang);
    if (!doc.visible && doc.value) {
      doc.value = false;
      uncheckedDocs.push(doc);
    }
  });
  return uncheckedDocs;
}
